import type React from "react"
import { useTranslation } from "react-i18next"
import { useState, useEffect, useMemo, useCallback } from "react"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  TextField,
} from "@mui/material"
import Autocomplete from "@mui/material/Autocomplete"
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  SwapHoriz as SwapHorizIcon,
  Videocam as VideocamIcon,
  Warning as WarningIcon,
} from "@mui/icons-material"
import EntityList from "../../components/EntityList"
import api from "../../services/api"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation, useAppNavigation } from "../../App"
import { Stall, Stable, Tenant } from "../../interfaces/index"

interface StallListProps {
  tenant_id?: number
  stable_id?: number
}

interface HorseOption {
  horseId: number
  horseName: string
  horseGender?: string | null
}

interface MoveContext {
  horseId: number
  horseName: string
  fromStallId: number | null
  fromStallName: string | null
  isDisplaced?: boolean
}
type MoveMode = "replace" | "perfect-swap"
const StallList: React.FC<StallListProps> = ({ tenant_id, stable_id }) => {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()

  const tenantIdFromParams = getParam("tenantId")
  const stableIdFromParams = getParam("stableId")

  const finalTenantId = useMemo(() => {
    return tenant_id || Number(tenantIdFromParams) || 1
  }, [tenant_id, tenantIdFromParams])

  const finalStableId = useMemo(() => {
    return stable_id || Number(stableIdFromParams) || null
  }, [stable_id, stableIdFromParams])

  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  const [selectedStall, setSelectedStall] = useState<Stall | null>(null)
  const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)

  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [stable, setStable] = useState<Stable | null>(null)
  const [loadingTenant, setLoadingTenant] = useState<boolean>(true)
  const [loadingStable, setLoadingStable] = useState<boolean>(true)

  const [allStalls, setAllStalls] = useState<Stall[]>([])
  const [allHorses, setAllHorses] = useState<HorseOption[]>([])
  // Câmeras associadas por baia (stallId -> nomes das câmeras). Usado na coluna
  // "Câmera" e na regra de exclusão (baia só some sem cavalo E sem câmera).
  const [camerasByStall, setCamerasByStall] = useState<Record<number, string[]>>({})
  const [openMoveDialog, setOpenMoveDialog] = useState<boolean>(false)
  const [loadingMoveData, setLoadingMoveData] = useState<boolean>(false)
  const [savingMove, setSavingMove] = useState<boolean>(false)

  const [selectedHorseIdToMove, setSelectedHorseIdToMove] = useState<number | "">("")
  const [selectedTargetStallId, setSelectedTargetStallId] = useState<number | "">("")
  const [moveContext, setMoveContext] = useState<MoveContext | null>(null)

  const [moveWarning, setMoveWarning] = useState<string | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)
  const [moveMode, setMoveMode] = useState<MoveMode>("replace")

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
  const [snackbarMessage, setSnackbarMessage] = useState<string>("")
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info")

  const showSnackbar = useCallback(
    (message: string, severity: "success" | "info" | "warning" | "error" = "info") => {
      setSnackbarMessage(message)
      setSnackbarSeverity(severity)
      setSnackbarOpen(true)
    },
    [],
  )

  const handleNew = useCallback(() => {
    const params: any = { mode: "new", tenantId: String(finalTenantId) }

    if (finalStableId) {
      params.stableId = String(finalStableId)
    }

    navigateToPage("baias", params)
  }, [navigateToPage, finalTenantId, finalStableId])

  const handleEdit = useCallback(
    (stallId: number | string) => {
      const params: any = {
        mode: "edit",
        id: String(stallId),
        tenantId: String(finalTenantId),
      }

      if (finalStableId) {
        params.stableId = String(finalStableId)
      }

      navigateToPage("baias", params)
    },
    [navigateToPage, finalTenantId, finalStableId],
  )

  const handleBackToStables = useCallback(() => {
    navigateToPage("estabulos", { mode: "list", tenantId: String(finalTenantId) })
  }, [navigateToPage, finalTenantId])

  const handleBackHome = useCallback(() => {
    navigateToPage("dashboard")
  }, [navigateToPage])

  useEffect(() => {
    let mounted = true

    const fetchTenant = async () => {
      try {
        const response = await api.get(`/tenant/${finalTenantId}`)

        if (!mounted) return

        let tenantData = null

        if (response.data?.tenant) {
          tenantData = response.data.tenant
        } else if (response.data?.tenantId && response.data?.tenantName) {
          tenantData = response.data
        }

        setTenant(tenantData)
      } catch (error) {
        console.error("Erro ao carregar tenant:", error)
      } finally {
        if (mounted) {
          setLoadingTenant(false)
        }
      }
    }

    fetchTenant()

    return () => {
      mounted = false
    }
  }, [finalTenantId])

  // Carrega as câmeras do tenant e mapeia por baia (cameraStall -> stallId).
  useEffect(() => {
    let mounted = true
    api
      .get(`/camera/${finalTenantId}`)
      .then((res) => {
        if (!mounted) return
        const list: any[] = Array.isArray(res.data)
          ? res.data
          : res.data?.cameras || []
        const map: Record<number, string[]> = {}
        for (const cam of list) {
          const sid = Number(cam.cameraStall)
          if (!sid) continue
          // Identificador robusto: canal (sempre presente) + nome se houver.
          const name = (cam.cameraName || "").trim()
          const ch = cam.cameraChannel
          const channelLabel =
            ch != null && ch !== "" ? `${t("fields.channel")} ${ch}` : ""
          const label = name
            ? channelLabel
              ? `${name} (${channelLabel})`
              : name
            : channelLabel || t("fields.camera")
          ;(map[sid] ||= []).push(label)
        }
        setCamerasByStall(map)
      })
      .catch(() => {
        if (mounted) setCamerasByStall({})
      })
    return () => {
      mounted = false
    }
  }, [finalTenantId, refreshTrigger, t])

  useEffect(() => {
    if (!finalStableId) {
      setLoadingStable(false)
      return
    }

    let mounted = true

    const fetchStable = async () => {
      try {
        const response = await api.get(`/stable/${finalTenantId}`)

        if (!mounted) return

        let stablesData: Stable[] = []

        if (Array.isArray(response.data)) {
          stablesData = response.data
        } else if (Array.isArray(response.data?.stables)) {
          stablesData = response.data.stables
        }

        const currentStable = stablesData.find(
          (item) => Number(item.stableId) === Number(finalStableId),
        )

        if (currentStable) {
          setStable(currentStable)
        }
      } catch (error) {
        console.error("Erro ao carregar stable:", error)
      } finally {
        if (mounted) {
          setLoadingStable(false)
        }
      }
    }

    fetchStable()

    return () => {
      mounted = false
    }
  }, [finalTenantId, finalStableId])

  const formatGender = (gender: string | null | undefined) => {
    if (!gender) return ""

    switch (gender) {
      case "M":
        return t("common.male")
      case "F":
        return t("common.female")
      default:
        return gender
    }
  }

  const normalizeStallsResponse = (data: any): Stall[] => {
    if (Array.isArray(data)) return data

    if (Array.isArray(data?.stalls)) return data.stalls

    if (data && typeof data === "object") {
      const values = Object.values(data)
      const firstArray = values.find((item) => Array.isArray(item))
      return Array.isArray(firstArray) ? (firstArray as Stall[]) : []
    }

    return []
  }

  const normalizeHorsesResponse = (data: any): HorseOption[] => {
    if (Array.isArray(data)) return data

    if (Array.isArray(data?.horses)) return data.horses

    if (data && typeof data === "object") {
      const values = Object.values(data)
      const firstArray = values.find((item) => Array.isArray(item))
      return Array.isArray(firstArray) ? (firstArray as HorseOption[]) : []
    }

    return []
  }

  const loadMoveData = useCallback(async () => {
    try {
      setLoadingMoveData(true)
      setMoveError(null)

      const [stallsResponse, horsesResponse] = await Promise.all([
        api.get(`/stall/${finalTenantId}`),
        api.get(`/horse/${finalTenantId}`),
      ])

      let stallsData = normalizeStallsResponse(stallsResponse.data)
      const horsesData = normalizeHorsesResponse(horsesResponse.data)

      if (finalStableId) {
        stallsData = stallsData.filter(
          (stall) => Number(stall.stableId) === Number(finalStableId),
        )
      }

      setAllStalls(stallsData)
      setAllHorses(horsesData)

      return {
        stalls: stallsData,
        horses: horsesData,
      }
    } catch (error) {
      console.error("Erro ao carregar dados para troca:", error)
      setMoveError(t("stalls.errorLoadMoveData"))
      return {
        stalls: [],
        horses: [],
      }
    } finally {
      setLoadingMoveData(false)
    }
  }, [finalTenantId, finalStableId])

  const getHorseById = useCallback(
    (horseId: number | null | undefined) => {
      if (!horseId) return null

      return allHorses.find((horse) => Number(horse.horseId) === Number(horseId)) || null
    },
    [allHorses],
  )

  const getStallByHorseId = useCallback(
    (horseId: number | null | undefined) => {
      if (!horseId) return null

      return allStalls.find((stall) => Number(stall.horseId) === Number(horseId)) || null
    },
    [allStalls],
  )

  const updateStallHorse = useCallback(
    async (stall: Stall, horseId: number | null) => {
      const payload = {
        ...stall,
        horseId,
      }

      await api.put(`/stall/${finalTenantId}/${stall.stallId}`, payload)
    },
    [finalTenantId],
  )

  const handleOpenMoveDialog = useCallback(
    async (stall?: Stall | null) => {
      setMoveError(null)
      setMoveWarning(null)
      setSelectedHorseIdToMove("")
      setSelectedTargetStallId("")
      setMoveContext(null)
      setMoveMode("replace")

      const { horses } = await loadMoveData()

      if (stall?.horseId) {
        const horseExists = horses.find(
          (horse) => Number(horse.horseId) === Number(stall.horseId),
        )

        if (!horseExists) {
          setMoveError(
            t("stalls.horseNotFoundInStall", {
              stallName: stall.stallName,
              horseId: stall.horseId,
            }),
          )
          setOpenMoveDialog(true)
          return
        }

        setSelectedHorseIdToMove(Number(stall.horseId))
        setMoveContext({
          horseId: Number(horseExists.horseId),
          horseName: horseExists.horseName,
          fromStallId: stall.stallId,
          fromStallName: stall.stallName,
        })
      }

      setOpenMoveDialog(true)
    },
    [loadMoveData],
  )

  const handleSelectHorseToMove = useCallback(
    (horseId: number) => {
      const horse = allHorses.find((item) => Number(item.horseId) === Number(horseId))

      if (!horse) {
        setMoveError(t("stalls.horseNotFound"))
        return
      }

      const currentStall = allStalls.find(
        (stall) => Number(stall.horseId) === Number(horseId),
      )

      setSelectedHorseIdToMove(horseId)
      setSelectedTargetStallId("")
      setMoveWarning(null)
      setMoveError(null)
      setMoveContext({
        horseId: Number(horse.horseId),
        horseName: horse.horseName,
        fromStallId: currentStall?.stallId || null,
        fromStallName: currentStall?.stallName || null,
      })
    },
    [allHorses, allStalls],
  )

  const handleConfirmMove = useCallback(async () => {
  if (!moveContext) {
    setMoveError(t("stalls.selectHorseToMove"))
    return
  }

  if (!selectedTargetStallId) {
    setMoveError(t("stalls.selectDestStall"))
    return
  }

  const targetStall = allStalls.find(
    (stall) => Number(stall.stallId) === Number(selectedTargetStallId),
  )

  if (!targetStall) {
    setMoveError(t("stalls.destStallNotFound"))
    return
  }

  if (Number(moveContext.fromStallId) === Number(selectedTargetStallId)) {
    setMoveError(t("stalls.horseAlreadyInStall"))
    return
  }

  const sourceStall = moveContext.fromStallId
    ? allStalls.find((stall) => Number(stall.stallId) === Number(moveContext.fromStallId))
    : null

  const displacedHorseId = targetStall.horseId ? Number(targetStall.horseId) : null

  try {
    setSavingMove(true)
    setMoveError(null)
    setMoveWarning(null)

    if (displacedHorseId) {
      const displacedHorse = allHorses.find(
        (horse) => Number(horse.horseId) === Number(displacedHorseId),
      )

      if (!displacedHorse) {
        setMoveError(
          t("stalls.displacedHorseNotFound", {
            stallName: targetStall.stallName,
            horseId: displacedHorseId,
          }),
        )
        return
      }

      if (moveMode === "perfect-swap") {
        if (!sourceStall) {
          setMoveError(
            t("stalls.perfectSwapNeedsStall", { horseName: moveContext.horseName }),
          )
          return
        }

        const confirmed = window.confirm(
          t("stalls.confirmPerfectSwap", {
            horseName: moveContext.horseName,
            targetStall: targetStall.stallName,
            displacedHorse: displacedHorse.horseName,
            sourceStall: sourceStall.stallName,
          }),
        )

        if (!confirmed) return

        // 1. Limpa as duas baias para evitar erro de chave única horse_idxU
        await updateStallHorse(targetStall, null)
        await updateStallHorse(sourceStall, null)

        // 2. Reassocia invertido
        await updateStallHorse(targetStall, moveContext.horseId)
        await updateStallHorse(sourceStall, displacedHorseId)

        setOpenMoveDialog(false)
        setMoveContext(null)
        setSelectedHorseIdToMove("")
        setSelectedTargetStallId("")
        setRefreshTrigger((prev) => prev + 1)

        showSnackbar(
          t("stalls.swapDone", {
            horseName: moveContext.horseName,
            targetStall: targetStall.stallName,
            displacedHorse: displacedHorse.horseName,
            sourceStall: sourceStall.stallName,
          }),
          "success",
        )

        return
      }

      const confirmed = window.confirm(
        t("stalls.confirmReplace", {
          targetStall: targetStall.stallName,
          displacedHorse: displacedHorse.horseName,
          horseName: moveContext.horseName,
        }),
      )

      if (!confirmed) return

      // 1. Limpa a baia de destino
      await updateStallHorse(targetStall, null)

      // 2. Limpa a baia de origem do cavalo que está sendo movido
      if (sourceStall) {
        await updateStallHorse(sourceStall, null)
      }

      // 3. Coloca o cavalo escolhido na baia de destino
      await updateStallHorse(targetStall, moveContext.horseId)

      setRefreshTrigger((prev) => prev + 1)

      const wantsRelocateDisplaced = window.confirm(
        t("stalls.confirmRelocateDisplaced", {
          horseName: moveContext.horseName,
          targetStall: targetStall.stallName,
          displacedHorse: displacedHorse.horseName,
        }),
      )

      if (wantsRelocateDisplaced) {
        const { horses } = await loadMoveData()

        const updatedDisplacedHorse =
          horses.find((horse) => Number(horse.horseId) === Number(displacedHorse.horseId)) ||
          displacedHorse

        setMoveContext({
          horseId: Number(updatedDisplacedHorse.horseId),
          horseName: updatedDisplacedHorse.horseName,
          fromStallId: null,
          fromStallName: null,
          isDisplaced: true,
        })

        setSelectedHorseIdToMove(Number(updatedDisplacedHorse.horseId))
        setSelectedTargetStallId("")
        setMoveWarning(
          t("stalls.displacedNeedsStall", { horseName: updatedDisplacedHorse.horseName }),
        )

        showSnackbar(
          t("stalls.movedChooseStallForDisplaced", {
            horseName: moveContext.horseName,
            displacedHorse: updatedDisplacedHorse.horseName,
          }),
          "warning",
        )

        return
      }

      setOpenMoveDialog(false)
      setMoveContext(null)
      setSelectedHorseIdToMove("")
      setSelectedTargetStallId("")

      showSnackbar(
        t("stalls.movedDisplacedLeftWithout", {
          horseName: moveContext.horseName,
          targetStall: targetStall.stallName,
          displacedHorse: displacedHorse.horseName,
        }),
        "success",
      )

      return
    }

    // Baia destino livre
    if (sourceStall) {
      await updateStallHorse(sourceStall, null)
    }

    await updateStallHorse(targetStall, moveContext.horseId)

    setOpenMoveDialog(false)
    setMoveContext(null)
    setSelectedHorseIdToMove("")
    setSelectedTargetStallId("")
    setRefreshTrigger((prev) => prev + 1)

    showSnackbar(
      t("stalls.movedToStall", {
        horseName: moveContext.horseName,
        targetStall: targetStall.stallName,
      }),
      "success",
    )
  } catch (error: any) {
    console.error("Erro ao trocar cavalo de baia:", error)

    const backendError = error?.response?.data?.error

    if (backendError?.includes("Duplicate entry")) {
      setMoveError(t("stalls.horseStillAssociated"))
      return
    }

    setMoveError(t("stalls.errorMove"))
  } finally {
    setSavingMove(false)
  }
}, [
  moveContext,
  selectedTargetStallId,
  allStalls,
  allHorses,
  moveMode,
  updateStallHorse,
  loadMoveData,
  showSnackbar,
])

  const handleShowDetails = useCallback((stall: Stall): void => {
    setSelectedStall(stall)
    setOpenDetailsDialog(true)
  }, [])

  const handleDelete = useCallback(
    async (stallId: number) => {
      if (!window.confirm(t("stalls.confirmDelete"))) {
        return
      }

      try {
        await api.delete(`/stall/${finalTenantId}/${stallId}`)

        if (openDetailsDialog) {
          setOpenDetailsDialog(false)
        }

        setRefreshTrigger((prev) => prev + 1)
      } catch (err) {
        console.error("Erro ao excluir baia:", err)
        alert(t("stalls.errorDelete"))
      }
    },
    [finalTenantId, openDetailsDialog],
  )

  const occupiedTargetStall = useMemo(() => {
    if (!selectedTargetStallId) return null

    const stall = allStalls.find(
      (item) => Number(item.stallId) === Number(selectedTargetStallId),
    )

    if (!stall?.horseId) return null

    const horse = allHorses.find(
      (item) => Number(item.horseId) === Number(stall.horseId),
    )

    return {
      stall,
      horse,
    }
  }, [selectedTargetStallId, allStalls, allHorses])

  const columns = useMemo(
    () => [
      {
        id: "stallName",
        label: t("stalls.nameLabel"),
        minWidth: 150,
      },
      {
        id: "stableId",
        label: t("fields.stable"),
        minWidth: 120,
        format: (value: number, row: Stall) => row.stableName || t("stalls.stableFallback", { id: value }),
        hide: !!finalStableId || isMobile,
      },
      {
        id: "horseId",
        label: t("fields.horse"),
        minWidth: 170,
        mobileHideLabel: true,
        format: (value: number | null | undefined, row: Stall) => {
          if (!value) return "-"

          return (
            <Chip
              label={`${row.horseName || t("stalls.horseFallback", { id: value })} ${
                row.horseGender ? `(${formatGender(row.horseGender)})` : ""
              }`}
              color={row.horseGender === "F" ? "secondary" : "primary"}
              size="small"
              variant="outlined"
            />
          )
        },
      },
      {
        // Usa "stallId" (campo que existe na linha) porque o EntityList só
        // chama format quando row[column.id] !== undefined; um id inexistente
        // (ex.: cameraStall) deixaria a célula vazia.
        id: "stallId",
        label: t("fields.camera"),
        minWidth: 90,
        mobileHideLabel: true,
        format: (_value: unknown, row: Stall) => {
          const cams = camerasByStall[Number(row.stallId)] || []
          if (cams.length === 0) return "-"
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {cams.map((label, i) => (
                <Chip
                  key={i}
                  icon={<VideocamIcon />}
                  label={label}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          )
        },
      },
    ],
    [finalStableId, isMobile, camerasByStall, t],
  )

  const transformData = useCallback(
    (data: any): any[] => {
      let stalls = normalizeStallsResponse(data)

      if (finalStableId) {
        stalls = stalls.filter((stall) => Number(stall.stableId) === Number(finalStableId))
      }

      return stalls
    },
    [finalStableId],
  )

  return (
    <>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ flexWrap: "wrap" }}>
              {loadingTenant ? (
                <CircularProgress size={16} />
              ) : (
                <Typography
                  color="text.primary"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    maxWidth: { xs: "120px", sm: "300px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(() => {
                    const possibleNames = [
                      tenant?.tenantName,
                      (tenant as any)?.name,
                      (tenant as any)?.nome,
                      (tenant as any)?.razaoSocial,
                      (tenant as any)?.nomeFantasia,
                    ]
                    return possibleNames.find((name) => name && typeof name === "string") || `Tenant ${finalTenantId}`
                  })()}
                </Typography>
              )}

              {finalStableId &&
                (loadingStable ? (
                  <CircularProgress size={16} />
                ) : (
                  <Link
                    color="inherit"
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleBackToStables() }}
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  >
                    {stable?.stableName || t("fields.stable")}
                  </Link>
                ))}
            </Breadcrumbs>
          </Box>

          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleBackHome}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            {isMobile ? null : t("common.back")}
          </Button>
        </Box>
      </Paper>

      <EntityList
        title={finalStableId && stable ? t("stalls.titleWithStable", { stable: stable.stableName }) : t("nav.stalls")}
        endpoint={`/stall/${finalTenantId}`}
        columns={columns}
        addPath=""
        editPathPrefix=""
        idField="stallId"
        dataPath="stalls"
        onRowClick={handleShowDetails}
        refreshTrigger={refreshTrigger}
        transformData={transformData}
        useEncryptedNavigation={true}
        encryptedPageName="baias"
        onAddClick={handleNew}
        onEditClick={handleEdit}
        getRowDeleteBlockReason={(row: Stall) => {
          const hasHorse = row.horseId != null && String(row.horseId) !== ""
          const hasCamera =
            (camerasByStall[Number(row.stallId)] || []).length > 0
          if (hasHorse && hasCamera) return t("stalls.cannotDeleteHasHorseAndCamera")
          if (hasHorse) return t("stalls.cannotDeleteHasHorse")
          if (hasCamera) return t("stalls.cannotDeleteHasCamera")
          return null
        }}
        extraParams={{ tenantId: String(finalTenantId) }}
        extraHeaderActions={
          <Button
            startIcon={<SwapHorizIcon />}
            variant="outlined"
            color="primary"
            onClick={() => handleOpenMoveDialog(null)}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? t("stalls.swapShort") : t("stalls.moveHorse")}
          </Button>
        }
      />

      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedStall && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                {selectedStall.stallName}
              </Typography>

              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setOpenDetailsDialog(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                  >
                    {t("stalls.infoTitle")}
                  </Typography>

                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>ID:</strong> {selectedStall.stallId}
                    </Typography>

                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.name")}:</strong> {selectedStall.stallName}
                    </Typography>

                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.stable")}:</strong>{" "}
                      {selectedStall.stableName || t("stalls.stableFallback", { id: selectedStall.stableId })}
                    </Typography>

                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.status")}:</strong>{" "}
                      <Chip
                        label={selectedStall.horseId ? t("common.occupied") : t("common.available")}
                        color={selectedStall.horseId ? "primary" : "default"}
                        size="small"
                        variant="outlined"
                      />
                    </Typography>

                    {selectedStall.horseId && (
                      <>
                        <Typography
                          sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, mt: 1 }}
                        >
                          <strong>{t("fields.horse")}:</strong>{" "}
                          {selectedStall.horseName || t("stalls.horseFallback", { id: selectedStall.horseId })}
                        </Typography>

                        {selectedStall.horseGender && (
                          <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                            <strong>{t("fields.gender")}:</strong> {formatGender(selectedStall.horseGender)}
                          </Typography>
                        )}
                      </>
                    )}

                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, mt: 1 }}>
                      <strong>{t("fields.camera")}:</strong>{" "}
                      {(camerasByStall[Number(selectedStall.stallId)] || []).join(", ") || "-"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions
              sx={{
                p: { xs: 1.5, sm: 2 },
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Button
                onClick={() => setOpenDetailsDialog(false)}
                variant="outlined"
                sx={{ width: isMobile ? "100%" : "auto" }}
              >
                {t("common.close")}
              </Button>

              {selectedStall.horseId && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => {
                    setOpenDetailsDialog(false)
                    handleOpenMoveDialog(selectedStall)
                  }}
                  sx={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t('stalls.swapStallOfHorse')}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={openMoveDialog}
        onClose={() => {
          if (!savingMove) setOpenMoveDialog(false)
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{t("stalls.moveDialogTitle")}</Typography>

          <IconButton onClick={() => setOpenMoveDialog(false)} disabled={savingMove}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loadingMoveData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Alert severity="info">
                {t("stalls.moveDialogInfo")}
              </Alert>

              {moveWarning && (
                <Alert severity="warning" icon={<WarningIcon />}>
                  {moveWarning}
                </Alert>
              )}

              {moveError && <Alert severity="error">{moveError}</Alert>}

              <Autocomplete
                fullWidth
                disabled={savingMove || !!moveContext?.isDisplaced}
                options={allHorses}
                getOptionLabel={(horse) => {
                  const stall = getStallByHorseId(horse.horseId)
                  return `${horse.horseName}${stall ? ` — ${stall.stallName}` : ` — ${t("stalls.noStall")}`}`
                }}
                value={allHorses.find((h) => h.horseId === selectedHorseIdToMove) ?? null}
                onChange={(_, horse) => {
                  if (horse) handleSelectHorseToMove(horse.horseId)
                  else { setSelectedHorseIdToMove(""); setMoveContext(null); setMoveError(null) }
                }}
                noOptionsText={t("stalls.noHorseFound")}
                renderInput={(params) => (
                  <TextField {...params} label={t("stalls.horseToMove")} />
                )}
              />

              {moveContext && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("stalls.selectedHorse")}
                  </Typography>

                  <Typography variant="body2">
                    <strong>{t("fields.name")}:</strong> {moveContext.horseName}
                  </Typography>

                  <Typography variant="body2">
                    <strong>{t("stalls.currentStall")}:</strong>{" "}
                    {moveContext.fromStallName || t("stalls.noStall")}
                  </Typography>
                </Paper>
              )}
<FormControl component="fieldset" disabled={savingMove || !moveContext}>
  <FormLabel component="legend">{t("stalls.destStallActionLegend")}</FormLabel>

  <RadioGroup
    value={moveMode}
    onChange={(event) => setMoveMode(event.target.value as MoveMode)}
  >
    <FormControlLabel
      value="replace"
      control={<Radio />}
      label={t("stalls.deallocate")}
    />

    <FormControlLabel
      value="perfect-swap"
      control={<Radio />}
      label={t("stalls.swap")}
    />
  </RadioGroup>
</FormControl>
              <FormControl fullWidth disabled={savingMove || !moveContext}>
                <InputLabel>{t("stalls.destStall")}</InputLabel>
                <Select
                  label={t("stalls.destStall")}
                  value={selectedTargetStallId}
                  onChange={(event) => {
                    setSelectedTargetStallId(Number(event.target.value))
                    setMoveError(null)
                  }}
                >
                  {allStalls.map((stall) => {
                    const occupiedHorse = getHorseById(stall.horseId)

                    const isSameCurrentStall =
                      moveContext?.fromStallId &&
                      Number(moveContext.fromStallId) === Number(stall.stallId)

                    return (
                      <MenuItem
                        key={stall.stallId}
                        value={stall.stallId}
                        disabled={!!isSameCurrentStall}
                      >
                        {stall.stallName}
                        {occupiedHorse
                          ? ` — ${t("stalls.occupiedBy", { horseName: occupiedHorse.horseName })}`
                          : stall.horseId
                            ? ` — ${t("stalls.occupiedByMissingHorse", { id: stall.horseId })}`
                            : ` — ${t("stalls.availableSuffix")}`}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>

              {occupiedTargetStall?.stall?.horseId && (
  <Alert severity={occupiedTargetStall.horse ? "warning" : "error"}>
    {occupiedTargetStall.horse ? (
      moveMode === "perfect-swap" ? (
        <>
          <strong>{occupiedTargetStall.horse.horseName}</strong> {t("stalls.occupiedSwapHint")}
        </>
      ) : (
        <>
          <strong>{occupiedTargetStall.horse.horseName}</strong> {t("stalls.occupiedReplaceHint")}
        </>
      )
    ) : (
      <>
        {t("stalls.occupiedMissingHorseHint")}
      </>
    )}
  </Alert>
)}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, flexDirection: isMobile ? "column" : "row" }}>
          <Button
            variant="outlined"
            onClick={() => setOpenMoveDialog(false)}
            disabled={savingMove}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={savingMove ? <CircularProgress size={18} color="inherit" /> : <SwapHorizIcon />}
            onClick={handleConfirmMove}
            disabled={savingMove || loadingMoveData || !moveContext || !selectedTargetStallId}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            {savingMove ? t("common.saving") : t("stalls.confirmMoveButton")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default StallList